import React from 'react';

import { AlineTooltip } from 'src/components';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

export const CITATION_INDEX_COUNTER_NAME = 'markdown-citation-index';

export const counterResetStyles = {
  counterReset: CITATION_INDEX_COUNTER_NAME,
} as const;

const useStyles = makeStyles()(theme => ({
  counter: {
    '&::before': {
      content: `counter(${CITATION_INDEX_COUNTER_NAME})`,
      counterIncrement: CITATION_INDEX_COUNTER_NAME,
    },
  },
  referenceLink: {
    ...getTypographyForSx(theme.typography.body1),
    alignItems: 'center',
    background: theme.palette.primary.main,
    borderRadius: 4,
    boxSizing: 'border-box',
    color: theme.palette.common.white,
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: 10,
    height: 16,
    padding: theme.spacing(0, 0.5),
    textDecoration: 'none',
    verticalAlign: 'middle',
  },
}));

interface CitationProps extends React.HTMLAttributes<HTMLAnchorElement> {
  /** Optional ID for the element */
  id?: string;
  /** Click handler for the citation */
  onClickCitation?: (e: React.MouseEvent) => void;
  /** Tooltip text to display on hover */
  tooltip?: string;
}

/**
 * IMPORTANT!
 *
 * When using this component, you must add the `counterResetStyles` to the
 * parent component in order for the CSS counter to work correctly.
 *
 * The `Citation` component is a pure presentational component for rendering a
 * citation and is used by both AI Chat and AI Report. Those components that use
 * this component are responsible for the business logic of the citation.
 */
export function Citation({
  id,
  onClickCitation,
  tooltip = 'View citation',
  ...rest
}: CitationProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <AlineTooltip arrow placement="top" title={tooltip}>
      <a
        {...rest}
        className={classes.referenceLink}
        id={id}
        rel="noopener noreferrer"
        target="_blank"
        onClick={onClickCitation}
      >
        {/* Uses a CSS counter to display the citation index */}
        <span className={classes.counter} />
      </a>
    </AlineTooltip>
  );
}
